/**
 * @file 学びメモの「書き足したぶんを測る起点」を、画面を離れても保つ置き場である。
 *
 * @what: メモ × 日ごとに、起点 (その日に初めて開いた時点の本文と画像)・開き方の id・一度でも送ったか
 *   を sessionStorage に置く。読めない環境ではメモリの表に置く (そのページの間だけ保つ)。
 * @why: 起点をペインの中だけで持つと、本だなへ戻って開き直したり再読み込みしたりするたびに開き方の
 *   id が変わり、書きかけの行がその前の区画に残る。※なぜならサーバは同じ id の送信だけを置き換える
 *   ためである (2026-09-14 に発生)。同じタブで同じ日なら同じ id を使い続けるようにする。
 * @failure: sessionStorage の読み書きが失敗しても例外を出さず、メモリの表に切り替える。
 */

import type { NoteEntrySnapshot } from "./noteEntryDiff";

export interface NoteEntrySession {
  date: string;
  segmentId: string;
  base: NoteEntrySnapshot;
  sent: boolean;
}

const PREFIX = "study-note-entry:";
const memory = new Map<string, NoteEntrySession>();

function keyOf(familyId: string, noteId: string): string {
  return `${PREFIX}${familyId}:${noteId}`;
}

export function loadNoteEntrySession(familyId: string, noteId: string): NoteEntrySession | null {
  const key = keyOf(familyId, noteId);
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(key);
  } catch {
    // 読めない環境 (私的な閲覧など) はメモリの表を使う
    return memory.get(key) ?? null;
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as NoteEntrySession;
    if (typeof parsed.date === "string" && typeof parsed.segmentId === "string" && parsed.base) {
      return parsed;
    }
  } catch {
    // 壊れた値は無いものとして扱う
  }
  return null;
}

export function saveNoteEntrySession(
  familyId: string,
  noteId: string,
  session: NoteEntrySession,
): void {
  const key = keyOf(familyId, noteId);
  memory.set(key, session);
  try {
    window.sessionStorage.setItem(key, JSON.stringify(session));
  } catch {
    // 容量や権限で書けなくても、メモリの表に残る
  }
}

/** 開き方の id である。同じ id の送信はサーバが置き換えるので、起点を作るたびに新しくする。 */
export function newNoteEntrySegmentId(): string {
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
